/**
 * Demat OCR verification UI state for Investor Demat Details cards.
 * Ops can verify manually (before cron) or after OCR scores — same Verify button.
 */

import dayjs from 'dayjs';

const OCR_LAST_ERROR_LABELS = {
  no_demat_extracted: 'Could not read demat number from the document',
  protected_pdf: 'Document is password-protected or encrypted',
  ocr_timeout: 'Document scan timed out',
  download_failed: 'Could not download the CMR/CML file',
  ocr_upstream_error: 'Document scan service returned an error',
  unexpected_error: 'An unexpected error occurred during verification',
};

export function mapOcrLastError(code) {
  if (!code) return null;
  return OCR_LAST_ERROR_LABELS[code] || String(code);
}

function parseMatchScore(dematMatchScore) {
  if (dematMatchScore === null || dematMatchScore === undefined || dematMatchScore === '') {
    return null;
  }
  const score = Number(dematMatchScore);
  return Number.isFinite(score) ? score : null;
}

/** CMR/CML must exist before Ops can verify. */
export function hasDematCmrDocument(demat = {}) {
  const url = demat.cmr_document_url || demat.demat_url;
  return Boolean(url && String(url).trim());
}

/** Demat is verified only when all three backend fields are present. */
export function isDematFullyVerified(demat = {}) {
  return (
    demat.is_verified === true
    && demat.demat_verified_at != null
    && demat.demat_verified_by != null
  );
}

const MANUAL_VERIFY_HINT =
  'Open the CMR/CML document, match it with the saved demat number, then click Verify.';

/**
 * @param {object} demat
 * @returns {{
 *   kind: 'verified' | 'pending' | 'scored_100' | 'scored_partial' | 'manual_review' | 'unknown',
 *   badge: { label: string, color: string } | null,
 *   messageLines: string[],
 *   showVerify: boolean,
 *   details: { matchScore: string, savedDemat: string, ocrExtracted: string } | null,
 * }}
 */
export function getDematOcrUiState(demat = {}) {
  if (isDematFullyVerified(demat)) {
    return {
      kind: 'verified',
      badge: { label: 'Verified', color: 'green' },
      messageLines: [
        `Verified on ${dayjs(demat.demat_verified_at).format('DD MMM YYYY, hh:mm A')}.`,
      ],
      showVerify: false,
      details: null,
    };
  }

  const canVerify = hasDematCmrDocument(demat);
  const status = demat.ocr_processing_status;

  if (status === 'pending') {
    const lines = [
      'Demat verification is pending. The uploaded CMR/CML document will be scanned automatically in the background.',
    ];
    if (canVerify) {
      lines.push(MANUAL_VERIFY_HINT);
    }
    return {
      kind: 'pending',
      badge: { label: 'Verification pending', color: 'blue' },
      messageLines: lines,
      showVerify: canVerify,
      details: null,
    };
  }

  if (status === 'manual_review') {
    const lines = [
      'Automatic OCR verification failed after multiple attempts. Please open the CMR/CML document, verify the demat number manually, correct the demat number if needed, or re-upload a readable document.',
    ];
    const errorLabel = mapOcrLastError(demat.ocr_last_error);
    if (errorLabel) {
      lines.push(`Reason: ${errorLabel}`);
    }
    if (canVerify) {
      lines.push(MANUAL_VERIFY_HINT);
    }
    return {
      kind: 'manual_review',
      badge: { label: 'Manual review required', color: 'orange' },
      messageLines: lines,
      showVerify: canVerify,
      details: null,
    };
  }

  if (status === 'scored') {
    const score = parseMatchScore(demat.demat_match_score);
    if (score === 100) {
      return {
        kind: 'scored_100',
        badge: { label: 'Unverified', color: 'default' },
        messageLines: [
          'Demat Account Number is 100% matched with the uploaded CMR/CML document. Click below to mark this Demat Account as Verified.',
          'Match score: 100%',
        ],
        showVerify: canVerify,
        details: null,
      };
    }

    const scoreDisplay =
      demat.demat_match_score != null && demat.demat_match_score !== ''
        ? String(demat.demat_match_score)
        : score != null
          ? String(score)
          : '—';
    const saved = demat.demat_number || '—';
    const extracted = demat.ocr_extracted_demat_number || '—';

    return {
      kind: 'scored_partial',
      badge: { label: 'Unverified', color: 'gold' },
      messageLines: [
        'Demat Account Number does not fully match the uploaded CMR/CML document.',
        'Please cross-check the saved Demat Number with the uploaded document before verifying.',
        MANUAL_VERIFY_HINT,
      ],
      showVerify: canVerify,
      details: {
        matchScore: `${scoreDisplay}%`,
        savedDemat: saved,
        ocrExtracted: extracted,
      },
    };
  }

  return {
    kind: 'unknown',
    badge: canVerify ? { label: 'Unverified', color: 'default' } : null,
    messageLines: canVerify ? [MANUAL_VERIFY_HINT] : [],
    showVerify: canVerify,
    details: null,
  };
}
